import React from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { submitFoundingCustomer } from "../lib/api";
import { TRADES } from "../lib/content";

const SIZES = [
  { v: "1-5",    label: "1 – 5 people" },
  { v: "6-20",   label: "6 – 20" },
  { v: "21-50",  label: "21 – 50" },
  { v: "51-200", label: "51 – 200" },
  { v: "200+",   label: "200+" },
];

const TRADE_VALUE = { pest: "pest_control", landscape: "landscaping" };

function errorMessage(detail) {
  if (typeof detail === "string") return detail;
  return detail?.[0]?.msg || "Something went wrong. Try again.";
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="lr-label">{label}</span>
      {children}
    </label>
  );
}

export default function FoundingForm({ defaultTrade }) {
  const [state, setState] = React.useState("idle");
  const [message, setMessage] = React.useState("");
  const [form, setForm] = React.useState({
    full_name: "",
    email: "",
    company_name: "",
    trade: defaultTrade || "roofing",
    company_size: "1-5",
    primary_software: "",
    notes: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      const r = await submitFoundingCustomer(form);
      setState("success");
      setMessage(r.message || "You're on the list.");
    } catch (err) {
      setState("error");
      setMessage(errorMessage(err?.response?.data?.detail));
    }
  };

  if (state === "success") {
    return (
      <div
        className="bg-[#0B3D5C] text-white p-10"
        data-testid="founding-success"
      >
        <p className="kicker text-[#E07B3A]">You're in.</p>
        <h3 className="font-sans-tight font-extrabold text-[2rem] mt-3 leading-tight">
          We logged your spot, {form.full_name.split(" ")[0]}.
        </h3>
        <p className="font-serif-body text-white/85 mt-5 text-[1.05rem] leading-relaxed">
          Vega just dropped a note in your inbox. No sales call coming — we
          honored the brand promise. When your slot opens, we'll send a single
          email to start your 14-day shadow mode.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-[#E5E0D8] p-8 sm:p-10"
      data-testid="founding-form"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full name">
          <input
            required
            value={form.full_name}
            onChange={set("full_name")}
            className="lr-input"
            data-testid="founding-full-name"
            placeholder="Tom Whitaker"
          />
        </Field>
        <Field label="Work email">
          <input
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            className="lr-input"
            data-testid="founding-email"
            placeholder="tom@oakridge-roofing.com"
          />
        </Field>
        <Field label="Company">
          <input
            required
            value={form.company_name}
            onChange={set("company_name")}
            className="lr-input"
            data-testid="founding-company"
            placeholder="Oakridge Roofing"
          />
        </Field>
        <Field label="Primary software">
          <input
            value={form.primary_software}
            onChange={set("primary_software")}
            className="lr-input"
            data-testid="founding-software"
            placeholder="AccuLynx, JobNimbus, etc."
          />
        </Field>
        <Field label="Trade">
          <select
            value={form.trade}
            onChange={set("trade")}
            className="lr-input"
            data-testid="founding-trade"
          >
            {TRADES.map((t) => (
              <option key={t.slug} value={TRADE_VALUE[t.slug] || t.slug}>
                {t.label}
              </option>
            ))}
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Crew size">
          <select
            value={form.company_size}
            onChange={set("company_size")}
            className="lr-input"
            data-testid="founding-size"
          >
            {SIZES.map((s) => (
              <option key={s.v} value={s.v}>{s.label}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-5">
        <Field label="What's the most painful follow-up you keep missing? (optional)">
          <textarea
            rows={3}
            value={form.notes}
            onChange={set("notes")}
            className="lr-input resize-none"
            data-testid="founding-notes"
            placeholder="Aging quotes that go to crickets after day 7."
          />
        </Field>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="font-serif-body text-[13px] text-[#4A4A4A] max-w-md">
          No sales call. No calendar invite. We'll send one email when your
          slot opens.
        </p>
        <button
          type="submit"
          disabled={state === "loading"}
          className="btn btn-primary"
          data-testid="founding-submit"
        >
          {state === "loading" ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Sending
            </>
          ) : (
            <>
              Claim my founding spot <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
      {state === "error" && (
        <p
          className="mt-4 font-serif-body text-[14px] text-[#C25555]"
          data-testid="founding-error"
        >
          {message}
        </p>
      )}
    </form>
  );
}
